import { supabase } from "./supabase";

export const referralBonusService = {
  // Get current week range for bonus calculations
  async getCurrentWeekRange() {
    try {
      const { data, error } = await supabase.rpc("get_current_week_range");
      if (error) throw error;
      return data[0];
    } catch (error) {
      console.error("Error getting current week range:", error);
      throw error;
    }
  },

  // Calculate and store referral bonuses for a specific week
  async calculateWeeklyReferralBonuses(weekStart, weekEnd) {
    try {
      console.log(
        "🔢 Calculating referral bonuses for week:",
        weekStart,
        "to",
        weekEnd
      );

      const { data, error } = await supabase.rpc(
        "calculate_weekly_referral_bonuses",
        {
          p_week_start: weekStart,
          p_week_end: weekEnd,
        }
      );

      if (error) throw error;

      const result = data[0];
      console.log("✅ Bonus calculation result:", result);

      return {
        processedReferrals: result.processed_referrals,
        totalBonusAmount: result.total_bonus_amount,
        bonusesCreated: result.bonuses_created,
      };
    } catch (error) {
      console.error("Error calculating weekly referral bonuses:", error);
      throw error;
    }
  },

  // Get referral bonus summary for an affiliate
  async getReferralBonusSummary(affiliateName) {
    try {
      const { data, error } = await supabase.rpc("get_referral_bonus_summary", {
        p_referrer_name: affiliateName,
      });

      if (error) throw error;

      const result = data[0];
      return {
        totalUnpaidBonuses: parseFloat(result.total_unpaid_bonuses || 0),
        totalPaidBonuses: parseFloat(result.total_paid_bonuses || 0),
        currentWeekBonuses: parseFloat(result.current_week_bonuses || 0),
        totalReferredEarnings: parseFloat(result.total_referred_earnings || 0),
      };
    } catch (error) {
      console.error("Error getting referral bonus summary:", error);
      return {
        totalUnpaidBonuses: 0,
        totalPaidBonuses: 0,
        currentWeekBonuses: 0,
        totalReferredEarnings: 0,
      };
    }
  },

  // Mark bonuses as paid (for payout system)
  async markBonusesAsPaid(affiliateName, weekStart) {
    try {
      const { data, error } = await supabase.rpc("mark_referral_bonuses_paid", {
        p_referrer_name: affiliateName,
        p_week_start: weekStart,
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error("Error marking bonuses as paid:", error);
      throw error;
    }
  },

  // Get unpaid referral bonuses for an affiliate
  async getUnpaidBonuses(affiliateName) {
    try {
      const { data, error } = await supabase
        .from("affiliate_referral_bonuses")
        .select("*")
        .eq("referrer_name", affiliateName)
        .eq("is_paid", false)
        .order("week_start", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error("Error getting unpaid bonuses:", error);
      return [];
    }
  },

  // Calculate all pending bonuses for all weeks (for batch processing)
  async calculateAllPendingBonuses() {
    try {
      console.log("🔄 Calculating all pending referral bonuses...");

      // Get the last 12 weeks to cover any missing calculations
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 12 * 7); // 12 weeks ago

      const toUtcMidnight = (date) =>
        new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

      const weeks = [];
      const endDateUtc = toUtcMidnight(endDate);
      let currentWeekStart = toUtcMidnight(startDate);

      // Align to Monday in UTC to avoid timezone drift when serializing to ISO dates
      const daysToSubtract = (currentWeekStart.getUTCDay() + 6) % 7;
      currentWeekStart.setUTCDate(currentWeekStart.getUTCDate() - daysToSubtract);

      while (currentWeekStart.getTime() <= endDateUtc.getTime()) {
        const weekStart = new Date(currentWeekStart);
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setUTCDate(weekEnd.getUTCDate() + 6); // Sunday (UTC)

        weeks.push({
          start: weekStart,
          end: weekEnd,
        });

        currentWeekStart.setUTCDate(currentWeekStart.getUTCDate() + 7);
      }

      console.log(
        `📅 Processing ${weeks.length} weeks for bonus calculations...`
      );

      let totalProcessed = 0;
      let totalBonusAmount = 0;
      let totalBonusesCreated = 0;

      for (const week of weeks) {
        const result = await this.calculateWeeklyReferralBonuses(
          week.start.toISOString().split("T")[0],
          week.end.toISOString().split("T")[0]
        );

        totalProcessed += result.processedReferrals;
        totalBonusAmount += result.totalBonusAmount;
        totalBonusesCreated += result.bonusesCreated;
      }

      console.log(`✅ Bonus calculations complete:`, {
        weeksProcessed: weeks.length,
        totalProcessed,
        totalBonusAmount: totalBonusAmount.toFixed(2),
        totalBonusesCreated,
      });

      return {
        weeksProcessed: weeks.length,
        totalProcessed,
        totalBonusAmount,
        totalBonusesCreated,
      };
    } catch (error) {
      console.error("Error calculating all pending bonuses:", error);
      throw error;
    }
  },

  // **NEW: Manual trigger to force referral bonus calculations**
  async manuallyCalculateAllBonuses() {
    try {
      console.log("🚀 MANUAL REFERRAL BONUS CALCULATION - Starting...");

      // First, get all referral connections
      const { data: referralConnections, error: referralError } = await supabase
        .from("affiliate_referrals")
        .select("referrer_name, referee_name")
        .eq("status", "accepted");

      if (referralError) {
        console.error("Error fetching referral connections:", referralError);
        throw referralError;
      }

      console.log(
        `📊 Found ${referralConnections?.length || 0} referral connections`
      );

      if (!referralConnections || referralConnections.length === 0) {
        return {
          success: true,
          message:
            "No referral connections found. Please create referral connections in admin panel first.",
          referralConnections: 0,
          bonusesCalculated: 0,
          totalBonusAmount: 0,
        };
      }

      // Calculate bonuses for last 12 weeks
      const result = await this.calculateAllPendingBonuses();

      // Get final summary
      const { data: finalBonuses, error: bonusError } = await supabase
        .from("affiliate_referral_bonuses")
        .select("referrer_name, bonus_amount, is_paid");

      if (bonusError) {
        console.error("Error getting final bonus summary:", bonusError);
      }

      const bonusSummary = (finalBonuses || []).reduce((acc, bonus) => {
        if (!acc[bonus.referrer_name]) {
          acc[bonus.referrer_name] = { total: 0, unpaid: 0 };
        }
        acc[bonus.referrer_name].total += parseFloat(bonus.bonus_amount || 0);
        if (!bonus.is_paid) {
          acc[bonus.referrer_name].unpaid += parseFloat(
            bonus.bonus_amount || 0
          );
        }
        return acc;
      }, {});

      console.log("🎉 MANUAL BONUS CALCULATION COMPLETE:", {
        referralConnections: referralConnections.length,
        bonusesCalculated: result.totalBonusesCreated,
        totalBonusAmount: result.totalBonusAmount,
        bonusSummary,
      });

      return {
        success: true,
        referralConnections: referralConnections.length,
        bonusesCalculated: result.totalBonusesCreated,
        totalBonusAmount: result.totalBonusAmount,
        bonusSummary,
      };
    } catch (error) {
      console.error("❌ MANUAL BONUS CALCULATION FAILED:", error);
      throw error;
    }
  },

  // Get detailed referral bonus history
  async getReferralBonusHistory(affiliateName, limit = 10) {
    try {
      const { data, error } = await supabase
        .from("affiliate_referral_bonuses")
        .select("*")
        .eq("referrer_name", affiliateName)
        .order("week_start", { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data.map((bonus) => ({
        id: bonus.id,
        refereeName: bonus.referee_name,
        weekStart: bonus.week_start,
        weekEnd: bonus.week_end,
        refereeEarnings: parseFloat(bonus.referee_earnings),
        bonusPercentage: parseFloat(bonus.bonus_percentage),
        bonusAmount: parseFloat(bonus.bonus_amount),
        isPaid: bonus.is_paid,
        createdAt: bonus.created_at,
      }));
    } catch (error) {
      console.error("Error getting referral bonus history:", error);
      throw error;
    }
  },

  // Get weekly bonus breakdown for a specific week
  async getWeeklyBonusBreakdown(weekStart, weekEnd) {
    try {
      const { data, error } = await supabase
        .from("affiliate_referral_bonuses")
        .select("*")
        .gte("week_start", weekStart)
        .lte("week_end", weekEnd)
        .order("bonus_amount", { ascending: false });

      if (error) throw error;

      return data.map((bonus) => ({
        referrerName: bonus.referrer_name,
        refereeName: bonus.referee_name,
        refereeEarnings: parseFloat(bonus.referee_earnings),
        bonusAmount: parseFloat(bonus.bonus_amount),
        isPaid: bonus.is_paid,
      }));
    } catch (error) {
      console.error("Error getting weekly bonus breakdown:", error);
      throw error;
    }
  },

  // Helper function to format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(amount);
  },

  // Helper function to format week range
  formatWeekRange(weekStart, weekEnd) {
    const start = new Date(weekStart).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    const end = new Date(weekEnd).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${start} - ${end}`;
  },
};
